import React, { useState, useEffect } from 'react';
import { getApprovals, approveRequest, rejectRequest, deleteApproval } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { formatDate, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  Mail,
  User,
  MessageSquare,
  RefreshCw,
  Filter,
  BarChart3,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Loader2,
  Trash2,
} from 'lucide-react';

export const ApprovalList = () => {
  const { isManager } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [quickActionId, setQuickActionId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadApprovals();
  }, []);

  const loadApprovals = async () => {
    try {
      const response = await getApprovals();
      setApprovals(response.data);
    } catch (error) {
      console.error('Error loading approvals:', error);
      toast.error('فشل تحميل الموافقات');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadApprovals();
  };

  const handleApprove = async (approval = selectedApproval, quickAction = false) => {
    if (!approval) return;
    
    if (quickAction) {
      setQuickActionId(approval.id);
    } else {
      setProcessing(true);
    }
    
    try {
      await approveRequest(approval.id, comments || 'تمت الموافقة');
      toast.success('تمت الموافقة بنجاح ✓');
      setSelectedApproval(null);
      setComments('');
      loadApprovals();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('فشل الموافقة');
    } finally {
      setProcessing(false);
      setQuickActionId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedApproval || !comments) {
      toast.error('يرجى إدخال سبب الرفض');
      return;
    }
    setProcessing(true);
    try {
      await rejectRequest(selectedApproval.id, comments);
      toast.success('تم الرفض');
      setSelectedApproval(null);
      setComments('');
      loadApprovals();
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('فشل الرفض');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (approval) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${approval.title}"؟`)) {
      return;
    }
    
    setDeletingId(approval.id);
    try {
      await deleteApproval(approval.id);
      toast.success('تم الحذف بنجاح');
      loadApprovals();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('فشل الحذف');
    } finally {
      setDeletingId(null);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email':
        return Mail;
      case 'quote':
        return FileText;
      default:
        return FileText;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'email':
        return 'بريد إلكتروني';
      case 'quote':
        return 'عرض سعر';
      case 'drawing':
        return 'رسم';
      case 'submittal':
        return 'Submittal';
      default:
        return type;
    }
  };

  const pendingApprovals = approvals.filter((a) => a.status === 'pending');
  const approvedApprovals = approvals.filter((a) => a.status === 'approved');
  const rejectedApprovals = approvals.filter((a) => a.status === 'rejected');

  // Filter by type
  const filteredPending = filterType === 'all' 
    ? pendingApprovals 
    : pendingApprovals.filter(a => a.type === filterType);

  // Stats
  const stats = {
    pending: pendingApprovals.length,
    approved: approvedApprovals.length,
    rejected: rejectedApprovals.length,
    total: approvals.length,
    emails: pendingApprovals.filter(a => a.type === 'email').length,
    quotes: pendingApprovals.filter(a => a.type === 'quote').length,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            لوحة الموافقات
          </h1>
          <p className="text-muted-foreground mt-1">Manager Approval Dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-sm"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Badge variant="outline" className="text-lg px-4 py-2 rounded-sm bg-orange-500/10 text-orange-500 border-orange-500/20">
            <Clock className="h-4 w-4 mr-2" />
            {stats.pending} معلق
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-sm bg-orange-500/10">
                <Clock className="h-5 w-5 text-orange-500" />
              </div>
              <span className="font-mono text-2xl font-bold">{stats.pending}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">طلبات معلقة</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-sm bg-green-500/10">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <span className="font-mono text-2xl font-bold">{stats.approved}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">موافق عليها</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-sm bg-red-500/10">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <span className="font-mono text-2xl font-bold">{stats.rejected}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">مرفوضة</p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-sm bg-blue-500/10">
                <BarChart3 className="h-5 w-5 text-blue-500" />
              </div>
              <span className="font-mono text-2xl font-bold">{stats.total}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">إجمالي الطلبات</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filterType} onValueChange={setFilterType} className="w-full">
        <TabsList className="grid grid-cols-4 w-full max-w-md">
          <TabsTrigger value="all" className="flex items-center gap-2" data-testid="filter-all">
            <Filter className="h-4 w-4" />
            الكل ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2" data-testid="filter-email">
            <Mail className="h-4 w-4" />
            بريد ({stats.emails})
          </TabsTrigger>
          <TabsTrigger value="quote" className="flex items-center gap-2" data-testid="filter-quote">
            <FileText className="h-4 w-4" />
            عروض ({stats.quotes})
          </TabsTrigger>
          <TabsTrigger value="submittal" data-testid="filter-submittal">
            Submittal
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Pending Approvals */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            طلبات معلقة ({filteredPending.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPending.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">لا توجد طلبات معلقة</p>
              <p className="text-sm">No pending approvals</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPending.map((approval) => {
                const TypeIcon = getTypeIcon(approval.type);
                const isQuickProcessing = quickActionId === approval.id;
                
                return (
                  <div
                    key={approval.id}
                    className="p-4 rounded-sm bg-muted/30 border border-border hover:border-primary/40 transition-all"
                    data-testid={`approval-item-${approval.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 rounded-sm bg-orange-500/10">
                          <TypeIcon className="h-5 w-5 text-orange-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium">{approval.title}</h3>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <User className="h-4 w-4" />
                            <span>{approval.requested_by_name}</span>
                            <span className="text-xs">•</span>
                            <span className="text-xs font-mono">{formatDate(approval.created_at)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {approval.content_preview}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <Badge variant="outline" className="rounded-sm">
                          {getTypeLabel(approval.type)}
                        </Badge>
                        
                        {/* Quick Actions */}
                        {isManager() && (
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedApproval(approval)}
                              className="h-8 px-2"
                              data-testid={`view-btn-${approval.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(approval, true)}
                              disabled={isQuickProcessing}
                              className="h-8 px-2 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                              data-testid={`quick-approve-${approval.id}`}
                            >
                              {isQuickProcessing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ThumbsUp className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedApproval(approval);
                                setComments('');
                              }}
                              className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              data-testid={`quick-reject-${approval.id}`}
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(approval)}
                              disabled={deletingId === approval.id}
                              className="h-8 px-2 text-gray-500 hover:text-red-600 hover:bg-red-500/10"
                              data-testid={`delete-btn-${approval.id}`}
                            >
                              {deletingId === approval.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History - Processed Approvals */}
      {(approvedApprovals.length > 0 || rejectedApprovals.length > 0) && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight">
              السجل ({approvedApprovals.length + rejectedApprovals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="approved">
              <TabsList className="mb-4">
                <TabsTrigger value="approved" className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  موافق ({approvedApprovals.length})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  مرفوض ({rejectedApprovals.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="approved">
                <div className="space-y-2">
                  {approvedApprovals.slice(0, 10).map((approval) => {
                    const TypeIcon = getTypeIcon(approval.type);
                    return (
                      <div
                        key={approval.id}
                        className="p-3 rounded-sm bg-green-500/5 border border-green-500/20"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <TypeIcon className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{approval.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="text-xs rounded-sm bg-green-500/10 text-green-500 border-green-500/20">
                              موافق ✓
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              {formatDate(approval.resolved_at)}
                            </span>
                            {isManager() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(approval)}
                                disabled={deletingId === approval.id}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                              >
                                {deletingId === approval.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        {approval.comments && (
                          <p className="text-xs text-muted-foreground mt-1 mr-7">
                            💬 {approval.comments}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="rejected">
                <div className="space-y-2">
                  {rejectedApprovals.slice(0, 10).map((approval) => {
                    const TypeIcon = getTypeIcon(approval.type);
                    return (
                      <div
                        key={approval.id}
                        className="p-3 rounded-sm bg-red-500/5 border border-red-500/20"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <TypeIcon className="h-4 w-4 text-red-500" />
                            <span className="text-sm">{approval.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="text-xs rounded-sm bg-red-500/10 text-red-500 border-red-500/20">
                              مرفوض ✗
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              {formatDate(approval.resolved_at)}
                            </span>
                            {isManager() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(approval)}
                                disabled={deletingId === approval.id}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                              >
                                {deletingId === approval.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        {approval.comments && (
                          <p className="text-xs text-muted-foreground mt-1 mr-7">
                            💬 {approval.comments}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={!!selectedApproval}
        onOpenChange={() => {
          setSelectedApproval(null);
          setComments('');
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              مراجعة الطلب
            </DialogTitle>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4">
              <div className="p-4 rounded-sm bg-muted/30 border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="rounded-sm">
                    {getTypeLabel(selectedApproval.type)}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    من: {selectedApproval.requested_by_name}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {formatDate(selectedApproval.created_at)}
                  </span>
                </div>
                <h3 className="font-medium text-lg">{selectedApproval.title}</h3>
                <p className="text-muted-foreground mt-2 whitespace-pre-wrap">
                  {selectedApproval.content_preview}
                </p>
              </div>

              {isManager() && selectedApproval.status === 'pending' && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      التعليقات (مطلوب للرفض)
                    </label>
                    <Textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="أدخل تعليقاتك هنا..."
                      className="bg-input/50 border-input rounded-sm"
                      rows={3}
                      data-testid="comments-textarea"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedApproval(null);
                        setComments('');
                      }}
                      className="rounded-sm"
                    >
                      إلغاء
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleReject}
                      disabled={processing || !comments}
                      className="rounded-sm"
                      data-testid="reject-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      رفض
                    </Button>
                    <Button
                      onClick={() => handleApprove()}
                      disabled={processing}
                      className="rounded-sm bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      موافقة
                    </Button>
                  </div>
                </>
              )}

              {selectedApproval.status !== 'pending' && (
                <div className={`p-4 rounded-sm ${
                  selectedApproval.status === 'approved' 
                    ? 'bg-green-500/10 border border-green-500/20' 
                    : 'bg-red-500/10 border border-red-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {selectedApproval.status === 'approved' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className="font-medium">
                      {selectedApproval.status === 'approved' ? 'تمت الموافقة' : 'تم الرفض'}
                    </span>
                  </div>
                  {selectedApproval.comments && (
                    <p className="text-sm text-muted-foreground mt-2">
                      💬 {selectedApproval.comments}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalList;
